import store from '../reducers/storeRegistration';
import notificationActions from '../actions/notificationActionCreators';
import { buildPendingSelection } from '../utils/cabinetCsvExportSelection';
const CABINET_CSV_EXPORT_CONFIRM_MODAL_ID = 'cabinet_csv_export_confirm';
const buildSelectedFormTypes = (selection = {}) => Object.entries(selection).map(([formTypeIdentifier, contentPaths]) => ({
  formTypeIdentifier,
  contentPaths
}));
const validateSelection = (selection = {}) => {
  const entries = Object.entries(selection);
  if (!entries.length) {
    return 'At least one asset type must be selected.';
  }
  const hasInvalidEntry = entries.some(([, contentPaths]) => !Array.isArray(contentPaths) || !contentPaths.length);
  if (hasInvalidEntry) {
    return 'Each selected asset type must have at least one column selected.';
  }
  return '';
};
const mapCountsToFormTypes = (counts = []) => counts.map(({
  formTypeIdentifier,
  formTypeName,
  count
}) => ({
  formTypeIdentifier,
  formTypeName,
  count
}));
const showColumnSelectionModal = ({
  formTypes,
  totalCount,
  loadColumnOptions
}) => new Promise(resolve => {
  const selectionRef = {
    selection: buildPendingSelection(formTypes),
    validationError: '',
    isExportReady: false,
    isLoadingColumns: false,
    hasNoExportableColumns: false,
    columnsByFormType: {}
  };
  const dismissAndResolve = selectedFormTypes => {
    store.dispatch(notificationActions.dismiss(CABINET_CSV_EXPORT_CONFIRM_MODAL_ID));
    resolve(selectedFormTypes);
  };
  const openModal = () => {
    store.dispatch(notificationActions.block({
      id: CABINET_CSV_EXPORT_CONFIRM_MODAL_ID,
      modalKind: 'cabinetCsvExportColumns',
      headerMessage: 'Export to CSV',
      formTypes,
      totalCount,
      selectionRef,
      loadColumnOptions,
      leftButtonLabel: 'Cancel',
      leftButtonAction: async () => dismissAndResolve(null),
      rightButtonLabel: selectionRef.isExportReady ? 'Export' : 'Loading columns...',
      rightButtonDisabled: !selectionRef.isExportReady,
      rightButtonAction: async () => {
        if (!selectionRef.isExportReady) {
          return;
        }
        const validationError = validateSelection(selectionRef.selection);
        if (validationError) {
          selectionRef.validationError = validationError;
          store.dispatch(notificationActions.dismiss(CABINET_CSV_EXPORT_CONFIRM_MODAL_ID));
          openModal();
          return;
        }
        dismissAndResolve(buildSelectedFormTypes(selectionRef.selection));
      }
    }, {
      autoClose: false,
      showCloseButton: false
    }));
  };
  openModal();
});
export const evaluate = async (entity, entityName, source, content, parentContentPath, transientContext, nativeFunctions, sourceEvaluator) => {
  try {
    const prepareResult = await sourceEvaluator({
      type: 'cabinetCsvExportCountBehavior',
      cabinetIdentifier: transientContext === null || transientContext === void 0 ? void 0 : transientContext.cabinetIdentifier,
      tags: ['csvImport']
    });
    if (!(prepareResult !== null && prepareResult !== void 0 && prepareResult.success)) {
      await sourceEvaluator({
        type: 'validationErrorBehavior',
        errors: [(prepareResult === null || prepareResult === void 0 ? void 0 : prepareResult.message) || 'Unable to prepare csv export.']
      });
      return false;
    }
    const formTypes = mapCountsToFormTypes(prepareResult.counts);
    if (!prepareResult.totalCount || !formTypes.length) {
      store.dispatch(notificationActions.info({
        id: 'cabinet_csv_export_no_assets',
        headerMessage: 'CSV Export',
        message: 'No exportable assets found in this cabinet.'
      }));
      return false;
    }
    const loadColumnOptions = async formTypeIdentifiers => sourceEvaluator({
      type: 'cabinetCsvExportColumnOptionsBehavior',
      cabinetIdentifier: transientContext === null || transientContext === void 0 ? void 0 : transientContext.cabinetIdentifier,
      formTypeIdentifiers,
      tags: ['csvImport']
    });
    const selectedFormTypes = await showColumnSelectionModal({
      formTypes,
      totalCount: prepareResult.totalCount,
      loadColumnOptions
    });
    if (!selectedFormTypes) {
      return false;
    }
    store.dispatch(notificationActions.info({
      id: 'cabinet_csv_export_in_progress',
      headerMessage: 'CSV Export',
      message: 'Preparing forms for export, this can take a while for large cabinets...'
    }, {
      autoClose: false
    }));
    const exportResult = await sourceEvaluator({
      type: 'cabinetCsvExportBehavior',
      cabinetIdentifier: transientContext === null || transientContext === void 0 ? void 0 : transientContext.cabinetIdentifier,
      selectedFormTypes,
      tags: ['csvImport']
    });
    store.dispatch(notificationActions.dismiss('cabinet_csv_export_in_progress'));
    if (!(exportResult !== null && exportResult !== void 0 && exportResult.success) || !exportResult.downloadUrl) {
      await sourceEvaluator({
        type: 'validationErrorBehavior',
        errors: [(exportResult === null || exportResult === void 0 ? void 0 : exportResult.message) || 'CSV export failed. Please try again.']
      });
      return false;
    }
    window.location.href = exportResult.downloadUrl;
    return exportResult;
  } catch (error) {
    store.dispatch(notificationActions.dismiss('cabinet_csv_export_in_progress'));
    await sourceEvaluator({
      type: 'validationErrorBehavior',
      errors: [(error === null || error === void 0 ? void 0 : error.message) || 'CSV export failed. Please try again.']
    });
    return false;
  }
};
export default {
  evaluate
};